import React, { useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { selectNeedsReview, computeRanks, ordinal, type ReviewCatch, type MysteryFish } from '@shared/adjudication';
import CatchDetailDialog from './CatchDetailDialog';
import CatchStatusLegend from './CatchStatusLegend';
import ConfirmActionDialog from './ConfirmActionDialog';

const client = generateClient({ authMode: 'userPool' });

const SPECIES_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  OTHER: 'Other',
};

interface NeedsReviewPanelProps {
  submissions: ReviewCatch[];
  onRefresh: () => void;
  /** The event's mystery fish, for the same fallback the leaderboard uses. */
  mystery?: MysteryFish | null;
  /** True once results are published: rotation is then refused. */
  published?: boolean;
}

/**
 * Needs review list (#144 R2): placed or flagged catches that have not been
 * checked by a person, most important first (placed by rank, then flagged).
 * Clears as catches are checked, via the caller's onRefresh/loadData.
 */
const NeedsReviewPanel: React.FC<NeedsReviewPanelProps> = ({ submissions, onRefresh, mystery, published }) => {
  const [openCatchId, setOpenCatchId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [removedIds, setRemovedIds] = useState<Record<string, boolean>>({});
  const [pendingConfirm, setPendingConfirm] = useState<{ catchId: string; decision: 'APPROVED' | 'DISQUALIFIED'; reason?: string } | null>(null);
  const rowButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const ranks = computeRanks(submissions as any, mystery);
  const items = selectNeedsReview(submissions as any, ranks).filter((i) => !removedIds[i.catchItem.id]);

  const openItem = openCatchId ? submissions.find((s: any) => s.id === openCatchId) : null;

  const closeDialog = (catchId: string) => {
    setOpenCatchId(null);
    setTimeout(() => rowButtonRefs.current[catchId]?.focus(), 0);
  };

  const runAdjudicate = async (catchId: string, decision: 'APPROVED' | 'DISQUALIFIED', reason?: string) => {
    // Guards a double click: once a decision for this catch is in flight,
    // a second click (e.g. the dialog's button firing twice before the
    // disabled state re-renders) is a no-op, not a second request.
    if (actionLoading === catchId) return;
    // A4: a decision made while results are published changes what the public
    // sees, so it is confirmed first. Approve is non-destructive (destructive=false).
    if (published && !pendingConfirm) {
      setPendingConfirm({ catchId, decision, reason });
      return;
    }
    setPendingConfirm(null);
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    setError(null);
    // Optimistic removal: a Disqualify takes the row out of the Needs review
    // list straight away. If the server refuses, it is put back (below) along
    // with the refusal message, instead of leaving the admin thinking it is
    // settled when it is not.
    const wasOptimisticallyRemoved = decision === 'DISQUALIFIED';
    if (wasOptimisticallyRemoved) setRemovedIds((prev) => ({ ...prev, [catchId]: true }));
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        catchId,
        decision,
        disqualificationReason: reason,
        expectedStatus: sub?.status,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to update submission');
      }
      closeDialog(catchId);
      onRefresh();
    } catch (err: any) {
      const message = err.message || 'Failed to update submission';
      setError(message);
      setStatusMessage(message);
      if (wasOptimisticallyRemoved) {
        setRemovedIds((prev) => { const n = { ...prev }; delete n[catchId]; return n; });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const runReinstate = async (catchId: string) => {
    if (actionLoading === catchId) return;
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    setError(null);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'REINSTATE_CATCH',
        catchId,
        expectedStatus: sub?.status || 'DISQUALIFIED',
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to reinstate the catch');
      }
      closeDialog(catchId);
      onRefresh();
    } catch (err: any) {
      const message = err.message || 'Failed to reinstate the catch';
      setError(message);
      setStatusMessage(message);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="mb-6 rounded-lg bg-white p-4 shadow sm:p-6">
      <h2 className="text-lg font-bold text-gray-900">Needs review ({items.length})</h2>
      <div aria-live="polite" className="sr-only">
        {items.length === 0 ? 'Nothing left to review.' : `${items.length} catches need review.`}
      </div>
      <div aria-live="polite" className="sr-only">{statusMessage}</div>

      {pendingConfirm && (
        <ConfirmActionDialog
          title="This changes the published results."
          message="Continue?"
          confirmLabel={pendingConfirm.decision === 'APPROVED' ? 'Approve' : 'Disqualify'}
          destructive={pendingConfirm.decision === 'DISQUALIFIED'}
          onConfirm={() => runAdjudicate(pendingConfirm.catchId, pendingConfirm.decision, pendingConfirm.reason)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      <div className="mt-3">
        <CatchStatusLegend />
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">{error}</p>
      )}

      {items.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">Nothing left to review.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map(({ catchItem, rank, reasons }) => {
            const speciesLabel = SPECIES_LABELS[catchItem.species] || catchItem.species;
            return (
              <li key={catchItem.id} className="rounded-md border border-gray-200 p-3">
                <button
                  type="button"
                  ref={(el) => { rowButtonRefs.current[catchItem.id] = el; }}
                  onClick={() => setOpenCatchId(catchItem.id)}
                  disabled={actionLoading === catchItem.id}
                  className="min-h-[44px] w-full text-left focus:outline-none focus:ring-2 focus:ring-fishing-blue disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div className="font-medium text-gray-900">
                    {catchItem.competitorName || 'Unknown'}: {speciesLabel}, {catchItem.lengthCm}cm
                    {rank != null && <span className="ml-2 text-amber-700">Placed {ordinal(rank)}</span>}
                  </div>
                  {reasons.length > 0 && (
                    <ul className="mt-1 list-inside list-disc text-sm text-gray-600">
                      {reasons.map((reason, i) => <li key={i}>{reason}</li>)}
                    </ul>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {openItem && (
        <CatchDetailDialog
          catchItem={openItem as any}
          rank={ranks[openItem.id] ?? null}
          onApprove={(catchId) => runAdjudicate(catchId, 'APPROVED')}
          onDisqualify={(catchId, reason) => runAdjudicate(catchId, 'DISQUALIFIED', reason)}
          onReinstate={(catchId) => runReinstate(catchId)}
          actionLoading={actionLoading === openItem.id}
          onClose={() => closeDialog(openItem.id)}
          onRotated={() => onRefresh()}
          competitionPublished={!!published}
        />
      )}
    </div>
  );
};

export default NeedsReviewPanel;
